import { useEffect, useRef, useState } from "react";
import {
  type ColumnDef,
  type ColumnOrderState,
  type ColumnPinningState,
  type ColumnSizingState,
  type SortingState,
  type Updater,
} from "@tanstack/react-table";
import { useAsyncDebouncedCallback } from "@tanstack/react-pacer";
import Skeleton from "react-loading-skeleton";
import { UpdateViewField } from "../../types/enums";
import type {
  AddUpdateDataViewViewModel,
  GetPagedDataViewModel,
} from "../../types/viewModels/dataViews";
import type {
  ColumnKey,
  Entity,
  PersistTableState,
  TableGridProps,
} from "@repo/types/ui/tables";
import {
  useAddUpdateDataView,
  useGetDataViewById,
  useGetKeyValuePairGroups,
} from "../../hooks/queries";
import {
  DataViewGroup,
  DataViewGroupSkeleton,
  type DataViewFiltersProps,
} from "../dataViews";
import { Table } from "./Table";

export interface TableDataViewProps<T>
  extends TableGridProps<T>, DataViewFiltersProps {
  viewId: number | null;
  columns: ColumnDef<T>[];
  onHorizontalScroll?: (cb: (scrollLeft: number) => void) => void;
}

const TableDataView = <T extends Entity>({
  viewId,
  columns,
  entityType,
  onCellValueUpdated,
  searchParams,
  onHorizontalScroll,
  onSearchParamsChange,
}: TableDataViewProps<T>) => {
  const { data: view, isPending: isLoadingView } =
    useGetDataViewById<T>(viewId);
  const { mutateAsync: saveDataView } = useAddUpdateDataView<T>();

  // ADDED FOR CHECKBOX
  const selectionColumn: ColumnDef<T> = {
    id: "select",
    size: 40,
    enablePinning: true, // left me fix rahega
    header: ({ table }) => (
      <input
        type="checkbox"
        checked={table.getIsAllRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
        className="
        w-4 h-4 
        accent-indigo-600 
        cursor-pointer
        rounded-md
        border border-gray-300
        shadow-sm
        hover:scale-105
        focus:ring-2 focus:ring-indigo-500
        transition-all duration-150
      "
      />
    ),
    cell: ({ row }) => (
      <input
        type="checkbox"
        checked={row.getIsSelected()}
        onClick={(e) => e.stopPropagation()} // STOP row click trigger
        onChange={row.getToggleSelectedHandler()}
        className="
        w-4 h-4 
        accent-indigo-600 
        cursor-pointer
        rounded-md
        border border-gray-300
        shadow-sm
        hover:scale-105
        focus:ring-2 focus:ring-indigo-500
        transition-all duration-150
      "
      />
    ),
  };

  const finalColumns = [
    selectionColumn,
    ...columns.filter((col) => col.id !== "select"),
  ];

  // ADDED FOR CHECKBOX
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([
    "select",
    ...columns.map((col) => col.id!),
  ]);

  const [getPagedDataViewModel, setPagedDataViewModel] = useState<
    GetPagedDataViewModel<T>
  >({
    ViewId: viewId,
    EntityType: entityType,
    ColumnFilters: [],
    SearchKeywords: searchParams.keywords,
    Start: 0,
    Length: 50,
  });

  const { data: keyValuePairGroups, isLoading: isLoadingKeyValuePairGroups } =
    useGetKeyValuePairGroups(getPagedDataViewModel);

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "CreatedOn",
      desc: true,
    },
  ]);

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({
    select: 40, // checkbox column width
    ...columns.reduce(
      (acc, col) => {
        acc[col.id!] = col.size ?? 150;
        return acc;
      },
      {} as Record<string, number>,
    ),
  });
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>({
    left: [
      "select", // checkbox always fixed
      ...columns.filter((c) => c.enablePinning === true).map((c) => c.id!),
    ],
    right: [],
  });

  const initialTableState = useRef<string | null>(null);

  const handleSortingChange = (updater: Updater<SortingState>) => {
    onSearchParamsChange({
      ...searchParams,
      source: "filters",
      inProgress: {
        ...searchParams.inProgress,
        keywords: false,
        filters: true,
      },
    });

    setSorting((old) => {
      const nextSorting =
        typeof updater === "function" ? updater(old) : updater;

      setPagedDataViewModel((prev) =>
        prev
          ? {
              ...prev,
              OrderBy: (nextSorting?.[0]?.id as ColumnKey<T>) ?? null,
              OrderDesc: nextSorting?.[0]?.desc ?? false,
            }
          : prev,
      );

      return nextSorting;
    });
  };

  useEffect(() => {
    if (!view) return;

    let persistedTableState: PersistTableState | null = null;

    if (view.Settings) {
      try {
        persistedTableState = JSON.parse(view.Settings) as PersistTableState;
        initialTableState.current = serializeTableState(persistedTableState);
      } catch {
        persistedTableState = null;
      }
    }

    if (!persistedTableState) return;

    const sorting = persistedTableState.sorting || [];

    setSorting(sorting);
    setColumnOrder(() => {
      const savedOrder = persistedTableState.columnOrder || [];

      // ensure select always first
      return ["select", ...savedOrder.filter((id) => id !== "select")];
    });
    setColumnSizing(persistedTableState.columnSizing);
    setColumnPinning(() => {
      const savedOrder = persistedTableState.columnOrder || columnOrder || [];

      return {
        left: savedOrder.slice(0, 2),
        right: [],
      };
    });

    setPagedDataViewModel((prev) =>
      prev
        ? {
            ...prev,
            ColumnFilters: view.FilterBy ? JSON.parse(view.FilterBy) : [],
            GroupBy: view.GroupBy,
            Start: 0,
            Length: 50,
            OrderBy: (sorting?.[0]?.id as ColumnKey<T>) ?? null,
            OrderDesc: sorting?.[0]?.desc,
          }
        : prev,
    );
  }, [view]);

  const debouncedSaveTableState = useAsyncDebouncedCallback(
    async (persistTableState: PersistTableState) => {
      const updatedDataView: AddUpdateDataViewViewModel<T> = {
        Id: viewId,
        Settings: JSON.stringify(persistTableState),
        UpdateViewField: UpdateViewField.Settings,
      };

      await saveDataView(updatedDataView);
    },
    {
      wait: 1000,
    },
  );

  const serializeTableState = (state: PersistTableState) =>
    JSON.stringify({
      sorting: state.sorting ?? [],
      columnOrder: state.columnOrder ?? [],
      columnSizing: state.columnSizing ?? {},
      columnPinning: state.columnPinning ?? { left: [], right: [] },
    });

  useEffect(() => {
    const currentTableState: PersistTableState = {
      sorting,
      columnOrder,
      columnSizing,
      columnPinning,
    };

    const currentSerializedState = serializeTableState(currentTableState);

    if (
      initialTableState.current !== null &&
      initialTableState.current === currentSerializedState
    ) {
      return;
    }

    debouncedSaveTableState({
      sorting,
      columnOrder,
      columnSizing,
      columnPinning,
    });

    initialTableState.current = currentSerializedState;
  }, [
    sorting,
    columnOrder,
    columnSizing,
    columnPinning,
    debouncedSaveTableState,
  ]);

  useEffect(() => {
    if (
      searchParams.source == null ||
      isLoadingView ||
      isLoadingKeyValuePairGroups
    )
      return;

    onSearchParamsChange({
      ...searchParams,
      source: null,
      inProgress: {
        ...searchParams.inProgress,
        keywords: false,
        filters: false,
      },
    });
  }, [
    isLoadingView,
    isLoadingKeyValuePairGroups,
    onSearchParamsChange,
    searchParams,
  ]);

  useEffect(() => {
    setPagedDataViewModel((prev) =>
      prev
        ? {
            ...prev,
            SearchKeywords: searchParams.keywords,
            Start: 0,
          }
        : prev,
    );
  }, [searchParams.keywords]);

  useEffect(() => {
    if (!columnOrder || columnOrder.length === 0) return;

    const firstTwo = columnOrder.slice(0, 2); // 👈 dynamic

    setColumnPinning({
      left: firstTwo,
      right: [],
    });
  }, [columnOrder]);

  if (!view || isLoadingView) {
    return <Skeleton />;
  }

  if (view.GroupBy) {
    if (!keyValuePairGroups || isLoadingKeyValuePairGroups) {
      return <DataViewGroupSkeleton rows={3} columns={columns} />;
    }

    const kvgWithData = keyValuePairGroups.filter(
      (kvg) => kvg.LinkedEntityItemsCount && kvg.LinkedEntityItemsCount > 0,
    );

    return (
      <>
        {kvgWithData.map((kvg) => {
          return (
            <DataViewGroup<T>
              key={kvg.Id}
              onHorizontalScroll={onHorizontalScroll}
              // columns={columns}
              columns={finalColumns} // ADDED FOR CHECKBOX
              keyValuePair={kvg}
              getPagedDataViewModel={getPagedDataViewModel}
              entityType={entityType}
              onCellValueUpdated={onCellValueUpdated}
              sorting={sorting}
              onSortingChange={handleSortingChange}
              columnOrder={columnOrder}
              onColumnOrderChange={setColumnOrder}
              columnSizing={columnSizing}
              onColumnSizingChange={setColumnSizing}
              columnPinning={columnPinning}
              onColumnPinningChange={setColumnPinning}
            />
          );
        })}
      </>
    );
  } else {
    return (
      <Table<T>
        hasGroup={false}
        // columns={columns}
        columns={finalColumns} // ADDED FOR CHECKBOX
        getPagedDataViewModel={getPagedDataViewModel}
        entityType={entityType}
        onCellValueUpdated={onCellValueUpdated}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        columnOrder={columnOrder}
        onColumnOrderChange={setColumnOrder}
        columnSizing={columnSizing}
        onColumnSizingChange={setColumnSizing}
        columnPinning={columnPinning}
        onColumnPinningChange={setColumnPinning}
      />
    );
  }
};

export { TableDataView };
